import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@kognitos/lattice';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type AuthenticationMethodStepData } from '../schemas';

// For no-auth integrations, we use a simplified schema
const noAuthSchema = z.object({
  connectionName: z.string().min(1, 'Connection name is required'),
});

export interface INoAuthConnectionStepProps {
  integrations: Bdkv2BookDescriptor;
  defaultValues?: Partial<AuthenticationMethodStepData>;
  onSubmit: (data: AuthenticationMethodStepData) => void;
}

type NoAuthFormData = z.infer<typeof noAuthSchema>;

export function NoAuthConnectionStep({
  integrations: _app,
  defaultValues,
  onSubmit,
}: INoAuthConnectionStepProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  const form = useForm({
    resolver: zodResolver(noAuthSchema),
    defaultValues: {
      connectionName: defaultValues?.connectionName || '',
    },
  });

  const handleSubmit = (data: NoAuthFormData) => {
    // Ensure data conforms to Step1Data by providing empty authMethodId
    const step1Data: AuthenticationMethodStepData = {
      connectionName: data.connectionName,
      authMethodId: '', // No auth method for no-auth integrations
      createSameConnection: true, // No-auth integrations always use single connection
    };
    onSubmit(step1Data);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-6"
        id="connection-name-and-auth-method-form"
      >
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="connectionName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.label',
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={tIntegrations(
                      'integrations.addConnection.connectionName.placeholder',
                    )}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.description',
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}
